import React from 'react';
import { useBatches } from '../hooks/useBatches';
import BatchItem from './BatchItem';

interface BatchesPanelProps {
  bucketName: string;
  canDownload: boolean;
}

const BatchesPanel: React.FC<BatchesPanelProps> = ({ bucketName, canDownload }) => {
  const { batches, isLoading, error, refresh, deleteBatch } = useBatches(bucketName);

  return (
    <div className="bg-white shadow-sm rounded-xl overflow-hidden border-2 border-gray-200 hover:border-gray-300 transition-all duration-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-amber-500 rounded-lg flex items-center justify-center shadow-sm">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
                <path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Batches Created</h3>
              {!isLoading && !error && (
                <p className="text-xs text-gray-500">
                  {batches.length} archive{batches.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={refresh}
            disabled={isLoading}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50 transition-colors"
            aria-label="Refresh batches"
          >
            <svg className={`w-3.5 h-3.5 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Loading state */}
        {isLoading && batches.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mb-3" />
            <p className="text-sm text-gray-500">Loading archives…</p>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-red-700 font-medium">Failed to load archives</p>
                <p className="text-xs text-red-600 mt-0.5">{error}</p>
                <button
                  type="button"
                  onClick={refresh}
                  className="mt-2 text-xs text-red-600 underline hover:text-red-800"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && batches.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
                <path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <h4 className="text-sm font-medium text-gray-900 mb-1">No batches created yet</h4>
            <p className="text-xs text-gray-500">
              ZIP archives you create will appear here for re-download.
            </p>
          </div>
        )}

        {/* Batch list */}
        {!isLoading && !error && batches.length > 0 && (
          <div className="space-y-3">
            {batches.map((batch) => (
              <BatchItem
                key={batch.jobId}
                batch={batch}
                bucketName={bucketName}
                canDownload={canDownload}
                onDelete={deleteBatch}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BatchesPanel;
